import { FC, Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUploading, {
  ImageListType,
  ImageType as ImageUploadType,
} from 'react-images-uploading';
import {
  Box,
  Card,
  CardActionArea,
  lighten,
  Stack,
  styled,
  Tooltip,
  Typography,
  TypographyVariant,
} from '@mui/material';
import classNames from 'classnames';
import { v4 } from 'uuid';

import { Button, Icon } from '@components/base';
import useImageGroupData from '@hooks/imageHooks/useImageGroupData';
import { ImageType } from '@models/Image';

import ImageGroupDisplay, { ImageGroupDisplayProps } from './ImageGroupDisplay';
import UploadImageDialog, { UploadImageDialogProps } from './UploadImageDialog';

export type ImageMap = Record<
  ImageType,
  { createAspectRatios: ImageType[]; image?: ImageUploadType }
>;

const NextVariant = {
  h1: 'h2',
  h2: 'h3',
  h3: 'h4',
  h4: 'h5',
  h5: 'h6',
  h6: 'body1',
} satisfies Partial<Record<TypographyVariant, TypographyVariant>>;

export type ImageUploaderProps = Pick<
  ImageGroupDisplayProps,
  'displayMode' | 'hideFallback' | 'hideMissing' | 'spacing'
> &
  Pick<UploadImageDialogProps, 'onSuccess' | 'hasUrgentPublishingOption'> & {
    alwaysDisplayDefaultImages?: boolean;
    clearImages?: VoidFunction;
    heading?: string;
    headingVariant?: keyof typeof NextVariant;
    hideUploadLabel?: boolean;
    location?: string;
    uploadLabel?: string;
    width?: number;
  };

const DropZone = styled(Card)`
  flex: 1;
  box-shadow: none;
  opacity: 0.8;
  border: none;
  outline: ${({ theme }) => `3px dashed ${theme.palette.divider}`};
  outline-offset: -3px;

  &.dragging {
    background: ${({ theme }) => theme.palette.info[theme.palette.mode]};
    color: ${({ theme }) => theme.palette.getContrastText(theme.palette.info[theme.palette.mode])};
    outline-color: ${({ theme }) => lighten(theme.palette.info[theme.palette.mode], 0.5)};
  }

  &:hover,
  &.dragging,
  &:focus-visible {
    opacity: 1;
  }
`;

const ImageUploader: FC<ImageUploaderProps> = ({
  alwaysDisplayDefaultImages,
  clearImages,
  displayMode,
  hasUrgentPublishingOption,
  heading,
  headingVariant = 'h3',
  hideFallback,
  hideUploadLabel,
  hideMissing,
  location,
  onSuccess,
  spacing = 2,
  uploadLabel,
  width,
}) => {
  const { t } = useTranslation();
  const { hasContextImages } = useImageGroupData();

  const displayDefaultImages = alwaysDisplayDefaultImages || hasContextImages;

  const [image, setImage] = useState<ImageUploadType>();
  const [loading, setLoading] = useState(false);

  const handleSelectImage = (imageList: ImageListType) => setImage(imageList[0]);
  const closeDialog = () => setImage(undefined);

  const inputId = useMemo(() => v4(), []);

  const nextHeadingVariant: TypographyVariant = heading
    ? NextVariant[headingVariant]
    : headingVariant;

  return (
    <Fragment>
      <Box>
        {heading && (
          <Stack alignItems='center' direction='row' mb={0.5} spacing={1}>
            <Typography gutterBottom variant={headingVariant}>
              {heading}
            </Typography>

            {clearImages && location && hasContextImages && (
              <Tooltip title={t('images.clear')}>
                <Button onClick={clearImages}>{t('clear')}</Button>
              </Tooltip>
            )}
          </Stack>
        )}

        {!hideUploadLabel && (
          <Box mb={0.5}>
            <Typography
              component='label'
              fontWeight='bold'
              htmlFor={inputId}
              variant={nextHeadingVariant}
            >
              {uploadLabel ?? t('image.upload.label')}
            </Typography>
          </Box>
        )}

        <Stack maxWidth={1} spacing={spacing} width={{ xs: 1, sm: width }}>
          <Box maxWidth={1} width={{ xs: 1, sm: width ?? 1 }}>
            <ImageUploading
              inputProps={{ id: inputId }}
              onChange={handleSelectImage}
              value={image ? [image] : []}
            >
              {({ isDragging, dragProps, onImageUpload }) => (
                <DropZone
                  className={classNames({ dragging: isDragging })}
                  elevation={2}
                  square
                  {...(loading ? {} : dragProps)}
                >
                  <CardActionArea onClick={onImageUpload}>
                    <Box maxWidth={1} p={2} sx={{ '*': { pointerEvents: 'none' } }}>
                      <Typography display='inline-flex' gap={1} variant='body2'>
                        <Icon fontSize='small' name='cloudUpload' />{' '}
                        {t('images.upload.clickOrDrop')}
                      </Typography>
                    </Box>
                  </CardActionArea>
                </DropZone>
              )}
            </ImageUploading>
          </Box>

          {displayDefaultImages && (
            <Box>
              <ImageGroupDisplay
                displayMode={displayMode}
                headingVariant={nextHeadingVariant}
                hideFallback={hideFallback}
                hideMissing={hideMissing}
                spacing={spacing}
              />
            </Box>
          )}
        </Stack>
      </Box>

      {image && (
        <UploadImageDialog
          hasUrgentPublishingOption={hasUrgentPublishingOption}
          image={image}
          loading={loading}
          location={location}
          onClose={closeDialog}
          onSuccess={onSuccess}
          setLoading={setLoading}
        />
      )}
    </Fragment>
  );
};

export default ImageUploader;
